// @ts-check
// invoice-parser.js
// Extracts structured invoice data from PDF, Excel, CSV, and plain text.
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import * as XLSX from 'xlsx';
import { askClaude } from './claude-client.js';
// Circular with invodiff-data (it uses lineTypeOf/ambiguousSkuSet from here) —
// legal ESM: both sides only call at runtime, never during module init.
import { normalizeForMatch, isAutoSku } from './invodiff-data.jsx';

// The worker ships in the bundle (?url) instead of from cdnjs — same version
// as the API by construction, covered by CSP worker-src 'self'.
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
}

// ── PDF extraction ────────────────────────────────────────────────────────
async function extractPDFText(file) {
  if (typeof pdfjsLib === 'undefined') throw new Error('PDF.js not loaded');
  const buffer = await file.arrayBuffer();
  // VULN (audit #6): PDF.js 3.11.174 is affected by CVE-2024-4367 — a crafted
  // PDF can execute arbitrary JS in the page origin. `isEvalSupported: false`
  // is Mozilla's documented mitigation and blocks the exploit. `isOffscreen…`
  // /font eval paths are the vector. NOTE: also upgrade the pinned CDN build to
  // a patched line (>= 4.2.67) — tracked as a manual follow-up in index.html.
  const pdf    = await pdfjsLib.getDocument({ data: buffer, isEvalSupported: false }).promise;

  // Pages are independent, so extract them all concurrently rather than waiting
  // for each page before starting the next. Promise.all preserves array order,
  // so the pages join back together in document order.
  const pages = await Promise.all(
    Array.from({ length: pdf.numPages }, (_, i) => i + 1).map(async (p) => {
      const page    = await pdf.getPage(p);
      const content = await page.getTextContent();

      // Group text items by rounded Y position to reconstruct lines
      const byY = {};
      // Marked-content items carry no transform/str, and getTextContent only
      // returns them when includeMarkedContent is set — it isn't here.
      for (const item of /** @type {import('pdfjs-dist/types/src/display/api').TextItem[]} */ (content.items)) {
        const y = Math.round(item.transform[5]);
        (byY[y] = byY[y] || []).push({ x: item.transform[4], str: item.str });
      }
      // The keys are rounded Y positions as strings; `b - a` coerces them.
      return /** @type {any[]} */ (Object.keys(byY))
        .sort((a, b) => b - a)
        .map(y => byY[y].sort((a, b) => a.x - b.x).map(i => i.str).join(' '))
        .join('\n');
    })
  );
  return pages.join('\n\n');
}

// ── Excel / CSV extraction ────────────────────────────────────────────────
async function extractExcelRows(file) {
  const buffer   = await file.arrayBuffer();
  const workbook = XLSX.read(buffer);
  const sheet    = workbook.Sheets[workbook.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
}

// ── Claude-based text parser (PDF / paste / Excel) ───────────────────────
const INVOICE_PARSE_SYSTEM = `You are an invoice data extractor. Given raw invoice or purchase order text (may be from a PDF, spreadsheet export, or plain text), work through the methodology below — internalize each step — then populate the fields of the response schema. The response format is enforced as JSON, so focus on extracting accurate values.

STEP 1 — DOCUMENT AUDIT
Identify silently before extracting anything:
- Document type (invoice, quote, purchase order, bill of lading, etc.)
- Whether this is a single document or several bundled in one file
- Whether line items continue across pages
- The header section (vendor, customer, date, document number)
- The footer section (subtotal, tax, shipping, grand total)
- Any continuation sheets, change orders, or alternates that should NOT be merged into the main item list

STEP 2 — LINE ITEM EXTRACTION
Read every priced row. For each, capture:
- Full raw description as written on the document
- Quantity — the quantity actually BILLED. When a row splits quantity into separate
  Ordered / Shipped / Back-ordered (B/O) columns (common on Porter Pipe and similar
  distributor invoices), use the SHIPPED quantity. Back-ordered (B/O) quantity is NOT
  invoiced and must be ignored — it does not contribute to the net amount. If only one
  quantity is shown, use it.
- Unit of measure (EA, BX, BDL, LN, LF, etc.)
- Part number / SKU if shown
- Unit price — the NET price actually charged per unit (after any discount), never the
  list / catalog / MSRP price. When a row shows a List/Unit Price column PLUS a separate
  Discount column (often a percentage like "84.440") and a Net Amount column, the net
  per-unit price = Net Amount ÷ shipped qty (equivalently List × (1 − discount%/100)).
  Example: 8 shipped, Price 21.170, Discount 84.440%, Net Amount 26.35 → net unit = 26.35/8 = 3.294.
- Extended price (Net Amount) — the printed line total actually billed (shipped qty × net
  unit price), AFTER discount and EXCLUDING back-ordered quantity

STEP 3 — TOTALS EXTRACTION (from the invoice footer)
Capture separately, exactly as printed:
- Stated subtotal (pre-tax merchandise total)
- Stated tax dollar amount
- Tax rate if shown
- Shipping / freight
- Stated grand total

STEP 4 — ARITHMETIC VERIFICATION (perform BEFORE emitting the result)
- For each line: verify qty × unit ≈ printed extended price. If they disagree, trust the printed extended price — PDF glyphs can be mis-read for the unit-price column — and back-compute unit = extended / qty.
- Sum every line's extended price. The sum MUST match the stated subtotal within rounding. If it does not, you have missed a line or misread a number — re-scan the document and locate the discrepancy before outputting.
- Confirm subtotal + tax + shipping ≈ stated grand total. If not, recheck the totals you captured.

FIELD RULES:
- taxRate is a decimal 0-1 (e.g. 8% = 0.08)
- shipping is a dollar amount, not a rate
- subtotal is the pre-tax merchandise/items total as printed in the invoice summary footer (labeled "Sub Total", "Merchandise", "Net", or similar); set to 0 only if truly not present
- taxTotal is the absolute tax dollar amount shown in the invoice summary footer (e.g. "Tax 634.11"); set to 0 only if truly not present
- total is the FINAL grand total / amount due exactly as printed in the summary footer (the "Total" / "Amount Due" / "Balance Due" line) AFTER every tax, shipping, discount, and adjustment. It is the AUTHORITATIVE billed amount. A document may carry adjustment lines that are NOT part of the merchandise subtotal — e.g. a restocking fee ("CUST RESTOCK"), handling, or misc charge — which are already reflected in the printed total. So capture the total exactly as printed even when subtotal + tax + shipping does not equal it. Example (return): subtotal -157.90, CUST RESTOCK +31.58, Total -126.32 means total = -126.32. Negative for a credit / return.
- qty is the SHIPPED / billed quantity. If the row breaks quantity into Ordered / Shipped / B/O (back-order) columns, use Shipped — never Ordered, and never add B/O. Back-ordered units are not billed.
- amount is the printed extended price (the "Net Amount" / "Amount" / "Ext" / "Extended" / "Total" column) — the line total ACTUALLY BILLED after discount and for the shipped qty only. Include it whenever such a column is present
- unit is the NET price per unit actually charged (after any discount). Derive it like this:
  1. If the row has an extended-price (Net Amount) column, compute unit = amount / qty (qty = shipped). This is the most reliable method because the extended price is the largest, clearest number on the line and least likely to be mis-read.
  2. Else if the row shows a List/Unit Price and a separate Discount % (e.g. 84.440), compute unit = listPrice × (1 − discount%/100).
  3. Otherwise use the "Net Price", "Your Price", "Contract Price", or "Sell Price" column.
  4. NEVER use the "List Price" / "Catalog Price" / pre-discount "Price" column as the unit — that is the pre-discount price and is wrong.
  Example A: [qty(shipped)=8, List=31.76, Net=10.3205, NetAmt=82.56] → unit=10.3205 (8×10.3205≈82.56 ✓).
  Example B (Porter-style % discount): [shipped=8, Price=21.170, Discount=84.440%, Net Amount=26.35] → unit = 26.35/8 = 3.294 (NOT 21.170).
- listPrice: the pre-discount List/Catalog/"Price" column value per unit — the number you must NOT put in the unit field. Capture it so the discount we received can be shown later. If there is no separate list column or no discount, set listPrice equal to unit. In Example B, listPrice = 21.170 (with unit = 3.294).
- sku: copy the part/item code EXACTLY as printed. If the document shows no part number for a line, use an empty string — NEVER invent, abbreviate, or derive a code from the description. Downstream matching treats the SKU as a hard identity; a made-up code corrupts it
- desc: replace any inch symbol with in and any foot symbol with ft
- Include EVERY priced line item — if your sum of items does not match the stated subtotal (STEP 4), you have missed at least one line
- uom: copy the unit of measure as printed (EA, BX, BDL, LN, PR, C, M, ...). The ONLY normalization allowed: footage spellings (LF, L.F., linear ft, linear feet, lineal ft, lin ft, FT) become "LF"
- If a line is priced per hundred (C) or per thousand (M), keep qty and unit exactly as printed with uom "C" or "M" — do NOT convert to each. Same for sticks/lengths (LN): keep the printed stick count, not derived footage
- For spreadsheet / tab-separated input, each row is a line item; use column headers to identify fields
- isCredit: set TRUE when the document is a credit memo / return / credit that REDUCES what is owed — signalled by a negative grand total, the words "Credit", "Credit Memo", "Return", "RA"/"RMA", "TO CORRECT ORIGINAL INVOICE", negative quantities, or amounts in parentheses/with a leading minus. A credit's qty, unit-extended amounts, subtotal, tax, and total all represent reductions; capture their magnitudes as printed (the app applies the negative sign) and set isCredit TRUE. Set FALSE for an ordinary invoice
- If a field is unknown use empty string or 0`;

// JSON Schema enforced via structured outputs (output_config.format). Every
// object sets additionalProperties:false and lists all fields as required, so
// the model must return exactly this shape — no missing keys, no extras, no
// markdown fences. Note: structured outputs ignore numeric/string constraints
// (min/max, minLength), so range rules like "taxRate is 0-1" stay in the prompt
// and in these descriptions rather than in the schema.
const INVOICE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    id:       { type: 'string', description: 'Invoice or PO number exactly as printed; empty string if none' },
    label:    { type: 'string', description: 'Either Invoice or Purchase Order' },
    isCredit: { type: 'boolean', description: 'TRUE if this is a credit memo / return / credit invoice (it REDUCES what is owed). Signals: a negative grand total, the words "Credit", "Credit Memo", "Return", "RA"/"RMA", "TO CORRECT ORIGINAL INVOICE", negative quantities, or amounts shown in parentheses/with a minus sign. FALSE for an ordinary invoice.' },
    vendor:   { type: 'string', description: 'Vendor / supplier name' },
    date:     { type: 'string', description: 'Document date as YYYY-MM-DD; empty string if not shown' },
    poRef:    { type: 'string', description: 'PO reference if present, else empty string' },
    currency: { type: 'string', description: 'ISO currency code, e.g. USD' },
    terms:    { type: 'string', description: 'Payment terms, e.g. Net 30; empty string if none' },
    items: {
      type: 'array',
      description: 'One entry per priced line item on the document',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          sku:    { type: 'string', description: 'Part / code EXACTLY as printed; empty string if none — never invented' },
          desc:   { type: 'string', description: 'Description; write inch marks as in and foot marks as ft' },
          qty:    { type: 'number', description: 'SHIPPED / billed quantity. If split into Ordered/Shipped/B-O columns, use Shipped (back-ordered qty is not billed)' },
          uom:    { type: 'string', description: 'Unit of measure as printed (EA, BX, ...); footage spellings normalized to LF' },
          unit:   { type: 'number', description: 'NET price per unit after discount; never list/MSRP. = Net Amount / shipped qty, or List x (1 - discount%/100)' },
          listPrice: { type: 'number', description: 'LIST / catalog unit price BEFORE discount (the pre-discount "Price"/"List" column). Capture it so the discount can be shown. If the row has no separate list column or no discount, set equal to unit.' },
          amount: { type: 'number', description: 'Printed extended Net Amount actually billed (shipped qty x net unit, after discount); 0 if no such column' },
        },
        required: ['sku', 'desc', 'qty', 'uom', 'unit', 'listPrice', 'amount'],
      },
    },
    shipping: { type: 'number', description: 'Shipping / freight dollar amount' },
    taxRate:  { type: 'number', description: 'Tax rate as a decimal 0-1 (8% = 0.08)' },
    subtotal: { type: 'number', description: 'Pre-tax merchandise subtotal from the footer; 0 if absent' },
    taxTotal: { type: 'number', description: 'Absolute tax dollar amount from the footer; 0 if absent' },
    total:    { type: 'number', description: 'The FINAL grand total / amount due EXACTLY as printed in the footer (the "Total" / "Amount Due" / "Balance Due" / "Total Due" line) — AFTER tax, shipping, discounts, restocking/handling fees, and every other adjustment. This is the authoritative billed amount: capture it as printed even when subtotal + tax + shipping does NOT equal it (e.g. a "CUST RESTOCK" restocking fee). Negative for a credit / return.' },
  },
  required: [
    'id', 'label', 'isCredit', 'vendor', 'date', 'poRef', 'currency', 'terms',
    'items', 'shipping', 'taxRate', 'subtotal', 'taxTotal', 'total',
  ],
};

// ── Post-parse price validation ───────────────────────────────────────────
// If Claude captured the line-total in `amount`, verify that qty × unit ≈ amount.
// When they diverge by more than 2 % (PDF font mis-read, wrong column chosen),
// trust the line-total and back-compute unit = amount / qty.
function validateItemPrices(result) {
  for (const item of result.items || []) {
    // Reconcile on MAGNITUDES so this also fires when a sign was split across
    // fields (e.g. a credit line returned as qty=+1 with amount=-X). Back-compute
    // unit = amount / qty, which carries the correct sign into qty × unit.
    if (item.amount !== 0 && item.qty !== 0) {
      const computed = item.qty * item.unit;
      if (Math.abs(computed - item.amount) / Math.abs(item.amount) > 0.02) {
        item.unit = +(item.amount / item.qty).toFixed(4);
      }
    }
    // listPrice is a positive per-unit reference (the pre-discount price). Keep it
    // a magnitude no smaller than the net unit — if it's missing or looks wrong,
    // fall back to the net unit (i.e. no discount).
    const u = Math.abs(item.unit || 0);
    item.listPrice = item.listPrice > 0 ? +(+item.listPrice).toFixed(4) : u;
    if (item.listPrice < u) item.listPrice = u;
  }

  // If the invoice states a pre-tax subtotal, compare it against the sum of
  // parsed line items. A significant mismatch means column mis-alignment or
  // missing items; flag it so the UI can warn the user. (Magnitude-based so it
  // works for credit memos with a negative stated subtotal too.)
  if (result.subtotal && Math.abs(result.subtotal) > 0) {
    const computedSubtotal = (result.items || []).reduce(
      (s, it) => s + it.qty * it.unit, 0
    );
    if (Math.abs(computedSubtotal - result.subtotal) / Math.abs(result.subtotal) > 0.02) {
      result._parseWarn = {
        computedSubtotal: +computedSubtotal.toFixed(2),
        statedSubtotal:   result.subtotal,
      };
    }
  }

  // `total` is the authoritative billed amount (incl. adjustments the subtotal
  // omits). If the model didn't capture one, derive it from the parts so every
  // invoice carries a usable total (a re-parse picks up the printed figure).
  if (!(Math.abs(result.total) > 0)) {
    const sub = result.subtotal ? +result.subtotal
      : (result.items || []).reduce((s, it) => s + it.qty * it.unit, 0);
    result.total = +(sub + (+result.taxTotal || 0) + (+result.shipping || 0)).toFixed(2);
  }
}

// Credit memos / returns REDUCE spend, so every monetary figure on them must be
// negative. The model's per-field sign handling is unreliable (it may drop the
// minus on qty but keep it on the amount, etc.), so once a document is judged a
// credit — by the model's isCredit flag OR a net-negative subtotal/line sum — we
// force the WHOLE invoice negative: qty negative, unit positive, every amount /
// subtotal / tax / shipping negative. Ordinary invoices (incl. ones with a lone
// negative discount line) are left untouched.
function normalizeCreditSign(result) {
  const items   = result.items || [];
  const lineSum = items.reduce((s, it) => s + (it.qty || 0) * (it.unit || 0), 0);
  const credit  = result.isCredit === true
    || (result.subtotal || 0) < -0.005
    || lineSum < -0.005;
  result.isCredit = credit;
  if (!credit) return;
  for (const it of items) {
    const aQty  = Math.abs(it.qty  || 0);
    const aUnit = Math.abs(it.unit || 0);
    const aAmt  = Math.abs(it.amount || 0) || +(aQty * aUnit).toFixed(2);
    it.qty    = -aQty;   // negative quantity → qty × unit is negative
    it.unit   =  aUnit;  // unit price stays a positive per-unit figure
    it.amount = -aAmt;
  }
  result.subtotal = -Math.abs(result.subtotal || 0);
  result.taxTotal = -Math.abs(result.taxTotal || 0);
  result.shipping = -Math.abs(result.shipping || 0);
  // The grand total is a reduction too — force it negative (a credit's printed
  // total already nets any restocking/adjustment, so this magnitude is correct).
  result.total    = -Math.abs(result.total || 0);
}

// ── Line classification ────────────────────────────────────────────────────
// Not every invoice line is a priced part. A lump-sum invoice (e.g. an Air
// Products damper package) lists the materials being supplied at $0 and bills
// the whole scope on one "all sum" line. We tag each line so downstream
// matching and price history treat it correctly instead of inventing per-unit
// prices or false materials:
//
//   material      — an ordinary priced line; matches AND feeds price history.
//   informational — a line with no usable price (a lump-sum component listed at
//                   $0); may still match by description to confirm delivery, but
//                   contributes nothing to price history.
//   aggregate     — a lump-sum / "all sum" total standing in for the whole
//                   package; kept for invoice totals but excluded from item
//                   matching and price tracking.
const AGGREGATE_DESC_RE = /\b(all[\s-]*sum|lump[\s-]*sum|lot[\s-]*price|package[\s-]*(price|total))\b/i;

// Pure derivation from the line's own fields. A persisted/overridden lineType
// (see lineTypeOf) takes precedence over this.
function deriveLineType(item) {
  const desc   = ((item && item.desc) || '').trim();
  const unit   = Math.abs(+(item && item.unit)   || 0);
  const amount = Math.abs(+(item && item.amount) || 0);
  if (AGGREGATE_DESC_RE.test(desc) && (unit > 0 || amount > 0)) return 'aggregate';
  if (!(unit > 0) && !(amount > 0)) return 'informational';
  return 'material';
}

// Honor a stored lineType (lets a future manual override stick), else derive.
function lineTypeOf(item) {
  return (item && item.lineType) || deriveLineType(item);
}

// A part code printed on several lines with DIFFERENT descriptions in one
// document is a series/family label (e.g. "DS-RUSKIN" stamped on every damper),
// not a unique part number. Return the set of such raw SKUs so callers can
// disable SKU-based matching for them — WITHOUT mutating the source SKU, so the
// document data stays exactly as parsed.
function ambiguousSkuSet(items) {
  const descsBySku = new Map();
  for (const it of items || []) {
    const sku = (it.sku || '').trim();
    if (!sku || isAutoSku(sku)) continue;
    let s = descsBySku.get(sku);
    if (!s) { s = new Set(); descsBySku.set(sku, s); }
    s.add(normalizeForMatch(it.desc || ''));
  }
  const out = new Set();
  for (const [sku, descs] of descsBySku) if (descs.size > 1) out.add(sku);
  return out;
}

// Stamp lineType + skuAmbiguous onto each parsed line so new invoices persist
// the classification. (Legacy invoices stored before this are re-derived on
// read by the consumers, so persistence is an optimization, not a dependency.)
function classifyInvoiceLines(result) {
  const items = result.items || [];
  const ambig = ambiguousSkuSet(items);
  for (const it of items) {
    it.lineType     = deriveLineType(it);
    it.skuAmbiguous = ambig.has((it.sku || '').trim());
  }
}

// ── Scanned-PDF OCR fallback (vision) ──────────────────────────────────────
// When a PDF has no text layer, render each page to a JPEG and let Claude read
// it. A vision-strong model (Sonnet, not the Haiku text default) is used for
// reliable OCR of the small, dense numbers on an invoice. Page count and
// resolution are capped to stay within the API's image and request-size limits.
const OCR_MODEL          = 'claude-sonnet-4-6';
const OCR_MAX_PAGES      = 10;
const OCR_PAGE_LONG_EDGE = 2000; // target px on a page's long edge

async function renderPDFToImages(file) {
  if (typeof pdfjsLib === 'undefined') throw new Error('PDF.js not loaded');
  const buffer = await file.arrayBuffer();
  const pdf    = await pdfjsLib.getDocument({ data: buffer, isEvalSupported: false }).promise;
  const count  = Math.min(pdf.numPages, OCR_MAX_PAGES);

  // Pages render independently — do them concurrently; Promise.all keeps order.
  const images = await Promise.all(
    Array.from({ length: count }, (_, i) => i + 1).map(async (p) => {
      const page     = await pdf.getPage(p);
      const unscaled = page.getViewport({ scale: 1 });
      const scale    = Math.min(3, OCR_PAGE_LONG_EDGE / Math.max(unscaled.width, unscaled.height));
      const viewport = page.getViewport({ scale });
      const canvas   = document.createElement('canvas');
      canvas.width   = Math.ceil(viewport.width);
      canvas.height  = Math.ceil(viewport.height);
      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      const data = canvas.toDataURL('image/jpeg', 0.82).split(',')[1];
      canvas.width = canvas.height = 0; // release the backing bitmap
      return { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data } };
    })
  );
  return images;
}

async function parseInvoiceImages(images) {
  if (!images.length) throw new Error('Could not render any pages from this PDF to read.');
  const raw = await askClaude({
    system: INVOICE_PARSE_SYSTEM,
    model:  OCR_MODEL,
    messages: [{
      role: 'user',
      content: [
        ...images,
        { type: 'text', text: 'These page image(s) are a scanned invoice or quote. Read them and extract the data per the system instructions.' },
      ],
    }],
    max_tokens: 8192,
    output_config: { format: { type: 'json_schema', schema: INVOICE_SCHEMA } },
  });

  const result = JSON.parse(raw);
  validateItemPrices(result);
  normalizeCreditSign(result);
  classifyInvoiceLines(result);
  result._ocr = true; // parsed from rendered page images, not a text layer
  return result;
}

// ── Public API ────────────────────────────────────────────────────────────
async function parseInvoiceFile(file) {
  const ext = file.name.split('.').pop().toLowerCase();

  if (['xlsx', 'xls'].includes(ext) || file.type.includes('spreadsheet')) {
    // Convert to tab-separated text and let Claude extract — far more accurate
    // than the heuristic row parser for real-world invoice layouts.
    const rows = await extractExcelRows(file);
    const text = rows.map((r) => r.join('\t')).join('\n');
    return parseInvoiceText(text);
  }
  if (ext === 'csv' || ext === 'tsv' || file.type.includes('csv')) {
    const text = await file.text();
    return parseInvoiceText(text);
  }
  if (ext === 'pdf' || file.type === 'application/pdf') {
    const text = await extractPDFText(file);
    // A scanned / photographed PDF has no text layer, so text extraction returns
    // (near-)nothing. Fall back to vision: render the pages to images and let
    // Claude read (OCR) and extract the line items directly from them.
    if (text.replace(/\s+/g, '').length < 20) {
      const images = await renderPDFToImages(file);
      return parseInvoiceImages(images);
    }
    return parseInvoiceText(text);
  }
  const text = await file.text();
  return parseInvoiceText(text);
}

async function parseInvoiceText(text) {
  // Backstop for any source (empty paste, blank spreadsheet, text-less PDF): an
  // empty request is rejected upstream with an opaque error, so fail clearly.
  if (!text || text.replace(/\s+/g, '').length < 20) {
    throw new Error('No readable text found in this document to extract line items from.');
  }
  const raw = await askClaude({
    system: INVOICE_PARSE_SYSTEM,
    messages: [{ role: 'user', content: text }],
    max_tokens: 8192,
    // Constrain the response to the invoice schema. Claude returns guaranteed
    // schema-valid JSON, which removes the markdown-fence stripping and the
    // malformed-JSON re-prompt this function used to need. (askClaude throws on
    // a refusal or a max_tokens truncation — the only ways the text would not be
    // valid schema JSON.)
    output_config: { format: { type: 'json_schema', schema: INVOICE_SCHEMA } },
  });

  const result = JSON.parse(raw);
  validateItemPrices(result);
  normalizeCreditSign(result);
  classifyInvoiceLines(result);
  result._rawText = text;
  return result;
}

export {
  parseInvoiceFile, parseInvoiceText, extractExcelRows,
  lineTypeOf, deriveLineType, ambiguousSkuSet, classifyInvoiceLines,
};
